import React, { useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import DraggableModal from './ui/DraggableModal';
import CustomOrderFilterForm from './CustomOrderFilterForm';

/* "Edit Filter" — reopens the header's Custom Filter modal from a results view,
 * seeded with the filter that produced it, so users can refine without going back
 * to the magnifier menu and rebuilding from scratch.
 *
 * Self-contained on purpose: SearchResults is large and untested, so the modal
 * state lives here where it can be exercised in isolation. */

interface Props {
  /** Active filter JSON (FilterCondition[]) that produced the current results. */
  filter: string;
  includeDeleted: boolean;
  /**
   * Active View id, carried in and back out again. Without it, refining a filter
   * would silently drop the layout the user is currently looking at.
   */
  viewId?: string;
  /** Fires with the edited filter; the host re-runs the search (e.g. via the URL). */
  onApply: (filterJson: string, includeDeleted: boolean, viewId: string) => void;
  /** Human-readable condition lines, shown as the button's tooltip. */
  summary?: string[];
}

const CustomFilterEditButton: React.FC<Props> = ({
  filter,
  includeDeleted,
  viewId = '',
  onApply,
  summary,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const apply = (filterJson: string, nextIncludeDeleted: boolean, nextViewId: string) => {
    setIsOpen(false);
    onApply(filterJson, nextIncludeDeleted, nextViewId);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title={summary?.length ? `Edit filter:\n${summary.join('\n')}` : 'Edit this filter'}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded transition-colors"
      >
        <FunnelIcon className="h-4 w-4" aria-hidden="true" />
        Edit Filter
      </button>

      {/* Remounted per open (key) so an abandoned edit doesn't persist: the form
          seeds its rows from initialFilter in useState, which otherwise survives
          a close/reopen cycle. */}
      <DraggableModal
        key={isOpen ? 'open' : 'closed'}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Custom Filter"
        width={980}
        topOffset={250}
      >
        <CustomOrderFilterForm
          onApply={apply}
          onCancel={() => setIsOpen(false)}
          initialFilter={filter}
          initialIncludeDeleted={includeDeleted}
          initialViewId={viewId}
        />
      </DraggableModal>
    </>
  );
};

export default CustomFilterEditButton;
